/**
 * P2 (AN-PP2) — leitura dos erros de elegibilidade na forma do interceptor `api`
 * (`RequisicaoFalhou`: `e.data.errorCode`, `e.data.data` com o detalhe). Fora do service de
 * propósito: os testes das telas mocam o service inteiro e estes helpers sumiriam.
 */
import { ERROS_ELEGIBILIDADE } from '@/types/elegibilidade'
import type { ImpedimentoEncaminhamento } from '@/types/elegibilidadeReserva'

// ─── Leitura dos erros da P2 (forma do interceptor `api`) ───

interface CorpoErro {
  errorCode?: string
  codigo?: string
  message?: string
  data?: unknown
}

const corpoDoErro = (e: unknown): CorpoErro | undefined =>
  (e as { data?: CorpoErro } | null | undefined)?.data ?? undefined

/** Código de erro do backend (`errorCode`, ou `codigo` no `ApiResponse` novo). */
export const codigoErroElegibilidade = (e: unknown): string | undefined => {
  const corpo = corpoDoErro(e)
  return corpo?.errorCode ?? corpo?.codigo ?? undefined
}

/**
 * 422 `ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO` → explicações + `politicaId`/`hashEntradas` para o
 * diálogo de sobreposição. `null` para qualquer outro erro.
 */
export const impedimentoDoErro = (e: unknown): ImpedimentoEncaminhamento | null => {
  if (codigoErroElegibilidade(e) !== ERROS_ELEGIBILIDADE.IMPEDE_ENCAMINHAMENTO) return null
  const dado = (corpoDoErro(e)?.data ?? {}) as Partial<ImpedimentoEncaminhamento>
  return {
    resultado: dado.resultado ?? null,
    explicacoes: Array.isArray(dado.explicacoes) ? dado.explicacoes : [],
    politicaId: dado.politicaId ?? null,
    politicaVersao: dado.politicaVersao ?? null,
    politicaTipo: dado.politicaTipo ?? null,
    hashEntradas: dado.hashEntradas ?? null,
    aviso: dado.aviso ?? null,
  }
}

/** Mensagens em português para os códigos de erro da P2 — sem código bruto na tela. */
export const MENSAGEM_ERRO_ELEGIBILIDADE: Readonly<Record<string, string>> = {
  [ERROS_ELEGIBILIDADE.CONGELADA]:
    'A reserva está com o parceiro (encaminhamento aguardando retorno ou aceito): os dados de elegibilidade não podem ser alterados agora.',
  [ERROS_ELEGIBILIDADE.SEM_IMPEDIMENTO]: 'A avaliação atual não tem impedimento a sobrepor.',
  [ERROS_ELEGIBILIDADE.AVALIACAO_MUDOU]:
    'A avaliação mudou desde que você abriu esta tela (dados ou política). Revise o novo resultado e decida de novo.',
  [ERROS_ELEGIBILIDADE.ROTA_NAO_APLICAVEL]: 'Este produto não tem rota de operação (só Antecipação de recebíveis).',
  [ERROS_ELEGIBILIDADE.MOTIVO_OBRIGATORIO]: 'Informe o motivo.',
  [ERROS_ELEGIBILIDADE.PRODUTO_DESCONHECIDO]: 'Produto desconhecido pelo módulo de oportunidades.',
  [ERROS_ELEGIBILIDADE.PARAMETROS_INCOMPATIVEIS_COM_TIPO]:
    'Os parâmetros informados não correspondem ao tipo de elegibilidade escolhido.',
  [ERROS_ELEGIBILIDADE.TIPO_FIXO_DO_PRODUTO]: 'Este produto tem tipo de elegibilidade fixo (Avaliação Originei).',
  [ERROS_ELEGIBILIDADE.VIGENCIA_RETROATIVA]: 'O início da vigência não pode ser no passado.',
  [ERROS_ELEGIBILIDADE.PRODUTO_RETIRADO]: 'Produto retirado do catálogo: não recebe nova política.',
  [ERROS_ELEGIBILIDADE.VIGENCIA_CONFLITANTE]:
    'A nova versão precisa começar depois do início da versão vigente (a anterior é encerrada na véspera). Se a vigente começou hoje, a nova vale a partir de amanhã.',
  [ERROS_ELEGIBILIDADE.PRODUTO_NAO_ENCONTRADO]: 'Produto não encontrado.',
  [ERROS_ELEGIBILIDADE.ORIGEM_OBRIGATORIA]: 'Informe a origem da política.',
  [ERROS_ELEGIBILIDADE.TIPO_OBRIGATORIO]: 'Escolha o tipo de elegibilidade.',
  [ERROS_ELEGIBILIDADE.JA_SOBREPOSTA]: 'O impedimento já foi sobreposto para estes dados e esta política.',
  [ERROS_ELEGIBILIDADE.DADOS_NAO_EDITAVEIS]:
    'A partir da Estruturação, os dados de elegibilidade são corrigidos pela Originei.',
  [ERROS_ELEGIBILIDADE.VALOR_IMOVEL_FONTE_INVALIDA]: 'Fonte do valor do imóvel inválida.',
  [ERROS_ELEGIBILIDADE.VALOR_INVALIDO]: 'Os valores de elegibilidade não podem ser negativos.',
  [ERROS_ELEGIBILIDADE.ROTA_OBRIGATORIA]: 'Escolha a nova rota.',
  [ERROS_ELEGIBILIDADE.ROTA_NAO_ALTERAVEL]:
    'A reserva está com o parceiro (encaminhamento aguardando retorno ou aceito) ou encerrada: a rota não pode ser alterada.',
  [ERROS_ELEGIBILIDADE.ROTA_INALTERADA]: 'A reserva já está nesta rota.',
}

/** Mensagem legível do erro: mapeada pelo código, senão a do servidor, senão o fallback. */
export const mensagemErroElegibilidade = (e: unknown, fallback: string): string => {
  const codigo = codigoErroElegibilidade(e)
  if (codigo && MENSAGEM_ERRO_ELEGIBILIDADE[codigo]) return MENSAGEM_ERRO_ELEGIBILIDADE[codigo]
  const msg = (e as { message?: unknown } | null | undefined)?.message
  return typeof msg === 'string' && msg ? msg : fallback
}
